import { type ChangeEvent, useCallback, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import {
  useIxfHoursV1MoneyMarketScheduleQueryServiceMoneyMarketScheduleGet,
  useIxfHoursV1MoneyMarketScheduleVersionQueryServiceMoneyMarketScheduleVersionGet,
  useIxfHoursV1TradingScheduleQueryServiceTradingScheduleGet,
  useIxfHoursV1TradingScheduleVersionQueryServiceTradingScheduleVersionGet,
  useIxfHoursV1TransfersScheduleVersionQueryServiceTransfersScheduleVersionGet,
} from '@interxfi/api-client/generated/hooks';
import { useIxfHoursV1TransfersScheduleQueryServiceTransfersScheduleGet } from '@interxfi/api-client/generated/hooks/ixf-hours-v1-transfers-schedule-query-service-transfers-schedule-get';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { SelectInput } from '@interxfi/design-system/components/input';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Typography } from '@interxfi/design-system/components/typography';
import type {
  IxfHoursV1MoneyMarketScheduleVersionState,
  IxfHoursV1Region,
  IxfHoursV1ScheduleExposure,
  IxfHoursV1TradingScheduleVersionState,
  IxfHoursV1TransfersScheduleVersionState,
} from '@interxfi/types';
import { NY_TIMEZONE } from '@interxfi/utilities/date/time-zones';
import { PageTabRail } from '../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../components/router/router-tabbed-navigation';
import { formatTimeZoneNameForDisplay, SCHEDULE_OPTIONS, type ScheduleName } from '../../../../../../features/hours/helpers';
import { getHoursSchedulePath, getScheduleNameFromSlug } from '../../../../../../features/hours/hours-schedule-routing';
import { HoursMoneyMarketScheduleEntityMenu } from '../../../../../../features/hours/money-market-schedule/hours-money-market-schedule-entity-menu';
import { HoursMoneyMarketScheduleTable } from '../../../../../../features/hours/money-market-schedule/hours-money-market-schedule-table';
import { HoursMoneyMarketScheduleHasPendingVersionStatusBar } from '../../../../../../features/hours/money-market-schedule/version/hours-money-market-schedule-has-pending-version-status-bar';
import { HoursMoneyMarketSchedulePendingVersionStatusBar } from '../../../../../../features/hours/money-market-schedule/version/hours-money-market-schedule-pending-version-status-bar';
import { useHoursScheduleContext } from '../../../../../../features/hours/schedule-context/hours-schedule-context';
import { HoursScheduleContextBar } from '../../../../../../features/hours/schedule-context/hours-schedule-context-bar';
import { HoursTradingScheduleEntityMenu } from '../../../../../../features/hours/trading-schedule/hours-trading-schedule-entity-menu';
import { HoursTradingScheduleTable } from '../../../../../../features/hours/trading-schedule/hours-trading-schedule-table';
import { HoursTradingScheduleHasPendingVersionStatusBar } from '../../../../../../features/hours/trading-schedule/version/hours-trading-schedule-has-pending-version-status-bar';
import { HoursTradingSchedulePendingVersionStatusBar } from '../../../../../../features/hours/trading-schedule/version/hours-trading-schedule-pending-version-status-bar';
import { HoursTransferScheduleEntityMenu } from '../../../../../../features/hours/transfer-schedule/hours-transfer-schedule-entity-menu';
import { HoursTransferScheduleTable } from '../../../../../../features/hours/transfer-schedule/hours-transfer-schedule-table';
import { HoursTransferScheduleHasPendingVersionStatusBar } from '../../../../../../features/hours/transfer-schedule/version/hours-transfer-schedule-has-pending-version-status-bar';
import { HoursTransferSchedulePendingVersionStatusBar } from '../../../../../../features/hours/transfer-schedule/version/hours-transfer-schedule-pending-version-status-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

const searchSchema = z.object({
  version: z.string().optional(),
});

const SCHEDULE_NAME_LABELS: Record<ScheduleName, string> = {
  trading: 'Trading',
  transfers: 'Transfers',
  moneyMarket: 'Money Markets',
};

export const Route = createFileRoute('/_app/_clearing-core/hours/_hours/schedule/{-$scheduleName}')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Hours,
      label: 'Schedules',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Hours>,
  validateSearch: searchSchema,
  head: ({ params }) => {
    const scheduleName = getScheduleNameFromSlug(params.scheduleName);

    return getPageHead([SCHEDULE_NAME_LABELS[scheduleName], 'Schedule'].filter(Boolean).join(' '));
  },
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'hours-schedules',
        label: 'Hours',
        to: getHoursSchedulePath(getScheduleNameFromSlug(params.scheduleName)),
      }),
    }) satisfies AggregateBreadcrumb,
  component: HoursScheduleDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load schedule', 'view schedule data');

interface ScheduleDetails<T extends ScheduleName> {
  kind: T;
  version: T extends 'trading'
    ? IxfHoursV1TradingScheduleVersionState | undefined
    : T extends 'transfers'
      ? IxfHoursV1TransfersScheduleVersionState | undefined
      : T extends 'moneyMarket'
        ? IxfHoursV1MoneyMarketScheduleVersionState | undefined
        : never;
  showPending: boolean;
  renderedVersionId: string | undefined;
  pendingVersionId: string | undefined;
  currentVersionId: string | undefined;
  timeZone: string | undefined;
}

function useScheduleDetails<T extends ScheduleName>(
  scheduleName: T,
  exposure: IxfHoursV1ScheduleExposure,
  region: IxfHoursV1Region,
  version: string | undefined,
): ScheduleDetails<T> {
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const {
    data: tradingResponse,
    isPending: tradingIsPending,
    error: tradingError,
  } = useIxfHoursV1TradingScheduleQueryServiceTradingScheduleGet(scheduleName === 'trading' ? { exposure, region } : undefined);
  const {
    data: transfersResponse,
    isPending: transfersIsPending,
    error: transfersError,
  } = useIxfHoursV1TransfersScheduleQueryServiceTransfersScheduleGet(scheduleName === 'transfers' ? { exposure, region } : undefined);
  const {
    data: moneyMarketResponse,
    isPending: moneyMarketIsPending,
    error: moneyMarketError,
  } = useIxfHoursV1MoneyMarketScheduleQueryServiceMoneyMarketScheduleGet(scheduleName === 'moneyMarket' ? { exposure, region } : undefined);
  const currentVersionId = match(scheduleName as ScheduleName)
    .returnType<string | undefined>()
    .with('trading', () => tradingResponse?.tradingSchedule?.data?.currentVersionId)
    .with('transfers', () => transfersResponse?.transfersSchedule?.data?.currentVersionId)
    .with('moneyMarket', () => moneyMarketResponse?.moneyMarketSchedule?.data?.currentVersionId)
    .otherwise(() => undefined);
  const pendingVersionId = match(scheduleName as ScheduleName)
    .returnType<string | undefined>()
    .with('trading', () => tradingResponse?.tradingSchedule?.data?.pendingVersionId)
    .with('transfers', () => transfersResponse?.transfersSchedule?.data?.pendingVersionId)
    .with('moneyMarket', () => moneyMarketResponse?.moneyMarketSchedule?.data?.pendingVersionId)
    .otherwise(() => undefined);
  const renderedVersionId = version ?? (activeTenantIsClearing ? (pendingVersionId ?? currentVersionId) : currentVersionId);

  const {
    data: tradingVersion,
    isPending: tradingVersionIsPending,
    error: tradingVersionError,
  } = useIxfHoursV1TradingScheduleVersionQueryServiceTradingScheduleVersionGet(
    scheduleName === 'trading' && renderedVersionId ? { tradingScheduleVersionId: renderedVersionId } : undefined,
  );
  const {
    data: transfersVersion,
    isPending: transfersVersionIsPending,
    error: transfersVersionError,
  } = useIxfHoursV1TransfersScheduleVersionQueryServiceTransfersScheduleVersionGet(
    scheduleName === 'transfers' && renderedVersionId ? { transfersScheduleVersionId: renderedVersionId } : undefined,
  );
  const {
    data: moneyMarketVersion,
    isPending: moneyMarketVersionIsPending,
    error: moneyMarketVersionError,
  } = useIxfHoursV1MoneyMarketScheduleVersionQueryServiceMoneyMarketScheduleVersionGet(
    scheduleName === 'moneyMarket' && renderedVersionId ? { moneyMarketScheduleVersionId: renderedVersionId } : undefined,
  );

  const error = match(scheduleName as ScheduleName)
    .returnType<Error | null>()
    .with('trading', () => tradingError || tradingVersionError)
    .with('transfers', () => transfersError || transfersVersionError)
    .with('moneyMarket', () => moneyMarketError || moneyMarketVersionError)
    .otherwise(() => null);
  const isPending = match(scheduleName as ScheduleName)
    .returnType<boolean>()
    .with('trading', () => tradingIsPending || tradingVersionIsPending)
    .with('transfers', () => transfersIsPending || transfersVersionIsPending)
    .with('moneyMarket', () => moneyMarketIsPending || moneyMarketVersionIsPending)
    .otherwise(() => false);

  useErrorHandler(error, errorMessageGetter);

  const showPending = Boolean(isPending || error);

  const versionState = match(scheduleName as ScheduleName)
    .returnType<
      IxfHoursV1TradingScheduleVersionState | IxfHoursV1TransfersScheduleVersionState | IxfHoursV1MoneyMarketScheduleVersionState | undefined
    >()
    .with('trading', () => tradingVersion?.tradingScheduleVersion)
    .with('transfers', () => transfersVersion?.transfersScheduleVersion)
    .with('moneyMarket', () => moneyMarketVersion?.moneyMarketScheduleVersion)
    .otherwise(() => undefined);

  const timeZone = versionState?.data?.week?.timezone;

  return {
    kind: scheduleName,
    version: versionState,
    showPending,
    currentVersionId,
    pendingVersionId,
    renderedVersionId,
    timeZone,
  } as ScheduleDetails<T>;
}

function HoursScheduleDetail() {
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const scheduleName = getScheduleNameFromSlug(params.scheduleName);
  const { version } = Route.useSearch();
  const { exposure, region, onChangeSchedule, schedule: contextScheduleName } = useHoursScheduleContext();
  const data = useScheduleDetails(scheduleName, exposure, region, version);
  const handleScheduleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newScheduleName = e.target.value as ScheduleName;
      onChangeSchedule(newScheduleName);
      void navigate({ to: getHoursSchedulePath(newScheduleName), search: { version: undefined } });
    },
    [onChangeSchedule, navigate],
  );

  useEffect(() => {
    if (scheduleName !== contextScheduleName) {
      onChangeSchedule(scheduleName);
    }
  }, [scheduleName, contextScheduleName, onChangeSchedule]);

  const isViewingPending = Boolean(data.pendingVersionId && data.renderedVersionId === data.pendingVersionId);
  const isViewingCurrentAndHasPending = Boolean(data.currentVersionId && data.renderedVersionId === data.currentVersionId && data.pendingVersionId);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageTabRail
          actions={
            <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
              {scheduleName === 'trading' ? (
                <HoursTradingScheduleEntityMenu
                  exposure={exposure}
                  region={region}
                  tradingScheduleVersionId={data.kind === 'trading' ? data.renderedVersionId : undefined}
                />
              ) : null}

              {scheduleName === 'transfers' ? (
                <HoursTransferScheduleEntityMenu
                  exposure={exposure}
                  region={region}
                  transferScheduleVersionId={data.kind === 'transfers' ? data.renderedVersionId : undefined}
                />
              ) : null}

              {scheduleName === 'moneyMarket' ? (
                <HoursMoneyMarketScheduleEntityMenu
                  exposure={exposure}
                  moneyMarketScheduleVersionId={data.kind === 'moneyMarket' ? data.renderedVersionId : undefined}
                  region={region}
                />
              ) : null}
            </HorizontalStack>
          }
          group={TabbedNavigationGroup.Hours}
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <HorizontalStack align="space-between" blockAlign="center" gap={2} wrap="nowrap">
          <SelectInput
            allowClear={false}
            hideLabel
            label="Schedule"
            matchWidth={false}
            name="schedule"
            onChange={handleScheduleChange}
            options={SCHEDULE_OPTIONS}
            placement="bottom-start"
            value={scheduleName}
            variant="button-light-small"
          />

          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            <Typography lineHeight="1.125rem" size="0.875rem">
              {data.showPending ? (
                <Skeleton charCount={NY_TIMEZONE.length} />
              ) : data.timeZone ? (
                formatTimeZoneNameForDisplay(data.timeZone)
              ) : (
                formatTimeZoneNameForDisplay(NY_TIMEZONE)
              )}
            </Typography>

            <HoursScheduleContextBar />
          </HorizontalStack>
        </HorizontalStack>
      </PageLayout.Section>

      {isViewingPending && data.pendingVersionId && data.currentVersionId ? (
        <>
          {scheduleName === 'trading' && data.kind === 'trading' ? (
            <HoursTradingSchedulePendingVersionStatusBar
              currentVersionId={data.currentVersionId}
              exposure={data.version?.exposure ?? exposure}
              pendingVersionId={data.pendingVersionId}
              region={data.version?.region ?? region}
            />
          ) : null}

          {scheduleName === 'transfers' && data.kind === 'transfers' ? (
            <HoursTransferSchedulePendingVersionStatusBar
              currentVersionId={data.currentVersionId}
              exposure={data.version?.exposure ?? exposure}
              pendingVersionId={data.pendingVersionId}
              region={data.version?.region ?? region}
            />
          ) : null}

          {scheduleName === 'moneyMarket' && data.kind === 'moneyMarket' ? (
            <HoursMoneyMarketSchedulePendingVersionStatusBar
              currentVersionId={data.currentVersionId}
              exposure={data.version?.exposure ?? exposure}
              pendingVersionId={data.pendingVersionId}
              region={data.version?.region ?? region}
            />
          ) : null}
        </>
      ) : null}

      {isViewingCurrentAndHasPending && data.pendingVersionId ? (
        <>
          {scheduleName === 'trading' && data.kind === 'trading' ? (
            <HoursTradingScheduleHasPendingVersionStatusBar pendingVersionId={data.pendingVersionId} />
          ) : null}

          {scheduleName === 'transfers' && data.kind === 'transfers' ? (
            <HoursTransferScheduleHasPendingVersionStatusBar pendingVersionId={data.pendingVersionId} />
          ) : null}

          {scheduleName === 'moneyMarket' && data.kind === 'moneyMarket' ? (
            <HoursMoneyMarketScheduleHasPendingVersionStatusBar pendingVersionId={data.pendingVersionId} />
          ) : null}
        </>
      ) : null}

      <PageLayout.Section>
        {scheduleName === 'trading' ? (
          <HoursTradingScheduleTable exposure={exposure} region={region} versionId={data.kind === 'trading' ? data.renderedVersionId : undefined} />
        ) : null}

        {scheduleName === 'transfers' ? (
          <HoursTransferScheduleTable
            exposure={exposure}
            region={region}
            versionId={data.kind === 'transfers' ? data.renderedVersionId : undefined}
          />
        ) : null}

        {scheduleName === 'moneyMarket' ? (
          <HoursMoneyMarketScheduleTable
            exposure={exposure}
            region={region}
            versionId={data.kind === 'moneyMarket' ? data.renderedVersionId : undefined}
          />
        ) : null}
      </PageLayout.Section>
    </PageLayout>
  );
}
